import React, { useEffect, useState } from "react";
import {
  failureMessageConstant,
  apiEndPointConstants,
  consentTypes,
  successMessageConstant
} from "../../../constants";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import Utils from "../../../utility";
import { hideLoader, showLoader } from "../../../slices/userSlice";
import ToggleSwitch from "../../../common/components/toggleSwitch";
import SubConsent from "./subConsent";
import PropTypes from "prop-types";

const { CONSENT } = apiEndPointConstants;
const { FILE_FORMAT_VALIDATION, FILE_SIZE_LIMIT } = failureMessageConstant;

const ConsentComponent = ({ consentListData, getConsentList }) => {
  const dispatch = useDispatch();
  const [consentList, setConsentList] = useState(consentListData);
  useEffect(() => {
    if (consentListData) {
      setConsentList(consentListData);
    }
  }, [consentListData]);
  const user = useSelector((state) => state.user);

  const [isSubConsentVisible, setIsSubConsentVisible] = useState(
    consentListData.map((consent) => {
      return consent.isActive
        ? consent.subConsentDetails.some(
            (subConsent) => subConsent.name.trim() !== ""
          )
        : false;
    })
  );
  const [toggleSwitches, setToggleSwitches] = useState(
    consentListData.map((consent) => consent.isRequired)
  );
  useEffect(() => {
    if (consentListData) {
      setConsentList(consentListData);
      setToggleSwitches(consentListData.map((consent) => consent.isRequired));
    }
  }, [consentListData]);

  const toggleSubConsentVisibility = (index) => {
    const updatedVisibility = [...isSubConsentVisible];
    updatedVisibility[index] = !updatedVisibility[index];
    setIsSubConsentVisible(updatedVisibility);
  };

  /**
   * Uploads a policy document file to the server.
   *
   * @param {File} file - The file object to be uploaded.
   * @param {Object} consent - The consent object containing information about the consent.
   * @param {number} index - The index of the consent object in the consent list.
   * @returns {Promise<void>} - A promise that resolves when the file is uploaded successfully.
   */
  const createFormData = (file, consent, isPolicyDoc) => {
    const subConsentNames = consent.subConsentDetails
      .filter((subConsent) => subConsent.name)
      .map((item, index) => ({
        name: item.name,
        index: index
      }));
    const formData = new FormData();
    formData.append("name", consent.name);
    formData.append("businessId", user._id);
    formData.append("isRequired", consent.isRequired);
    formData.append("originalFileName", file.name);
    formData.append("file", file);
    formData.append("isActive", consent.isActive);
    formData.append("isDeleted", false);
    formData.append("subConsents", JSON.stringify(subConsentNames));
    formData.append("isPolicyDoc", isPolicyDoc);
    return formData;
  };

  const updateProgress = (progressEvent, index, file) => {
    const progress = Math.round(
      (progressEvent.loaded / progressEvent.total) * 100
    );
    setConsentList((prevConsentList) =>
      prevConsentList.map((consent, consentIndex) =>
        consentIndex === index
          ? {
              ...consent,
              uploadProgress: progress,
              originalFileName: progress !== 100 ? "" : file.name
            }
          : consent
      )
    );
  };

  const createAxiosInstance = (index, file) => {
    return axios.create({
      onUploadProgress: (progressEvent) =>
        updateProgress(progressEvent, index, file)
    });
  };

  const handleResponse = async (response) => {
    if (
      response?.data?.responseCode === 200 &&
      Object.keys(response?.data?.responseData).length > 0
    ) {
      const consentData = response.data.responseData;
      setConsentList((prevConsentList) =>
        prevConsentList.map((consent) =>
          consent.name === consentData.name
            ? {
                ...consent,
                _id: consentData._id,
                originalFileName: consentData.originalFileName,
                policyText: consentData.policyText
              }
            : consent
        )
      );
      await getConsentList();
    }
  };

  const updateConsent = async (file, consent, index, isPolicyDoc) => {
    try {
      const formData = createFormData(file, consent, isPolicyDoc);
      const instance = createAxiosInstance(index, file);
      const updatedToggles = [...toggleSwitches];
      updatedToggles[index] === consent.isRequired && dispatch(showLoader());

      const response = await instance.post(
        `${process.env.REACT_APP_CONSENT_MICROSERVICE}${CONSENT}`,
        formData,
        Utils.getHeader()
      );

      await handleResponse(response);
      return response;
    } catch (error) {
      // If an error occurs, return without throwing an error
      console.error(error);
      return;
    } finally {
      dispatch(hideLoader());
    }
  };

  const checkIsActiveConsents = () => {
    const activeConsents = consentList.filter((consent) => consent.isActive);
    return activeConsents.length > 1;
  };

  /**
   * Updates the consent list based on the checkbox click event.
   *
   * @param {Event} event - The checkbox click event.
   * @param {number} index - The index of the consent in the list.
   * @return {void} No return value.
   */
  const onClickCheckBoxHandler = async (event, consentDetails, index) => {
    const isMoreThanOneActive = checkIsActiveConsents();
    if (!isMoreThanOneActive && !event.target.checked) {
      Utils.failureToastMessage(
        failureMessageConstant.AT_LEAST_ONE_CONSENT_SELECTED
      );
      return;
    }
    setConsentList((prevConsentList) => {
      const updatedConsentList = prevConsentList.map(
        (consent, consentIndex) => {
          if (consentIndex === index) {
            const updatedConsentDetails = {
              ...consentDetails,
              isActive: event.target.checked,
              isRequired: event.target.checked ? consent.isRequired : false
            };
            updateConsent("", updatedConsentDetails, index, false);
            return {
              ...consent,
              isActive: event.target.checked,
              uploadProgress:
                consent?.uploadProgress !== undefined
                  ? consent.uploadProgress
                  : 0
            };
          }
          return consent;
        }
      );
      return updatedConsentList;
    });
  };

  /**
   * A function that handles the upload event.
   *
   * @param {Event} event - The event object.
   * @param {Consent} consent - The consent object.
   * @param {number} index - The index of the upload.
   * @return {undefined} No return value.
   */
  const uploadHandler = async (event, consent, index) => {
    const file = event.target.files[0];
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB in bytes

    // Validate file format
    if (!file?.name.match(/\.(pdf|txt|docx)$/)) {
      Utils.failureToastMessage(FILE_FORMAT_VALIDATION);
      event.target.value = null;
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      Utils.failureToastMessage(FILE_SIZE_LIMIT);
      event.target.value = null;
      return;
    }

    event.target.value = null;
    await updateConsent(file, consent, index, true);
  };

  const truncateFileName = (fileName) => {
    switch (true) {
      case window.innerWidth <= 400:
        return Utils.truncateString(fileName);
      case window.innerWidth <= 450:
        return Utils.truncateString(fileName, 18);
      case window.innerWidth <= 550:
        return Utils.truncateString(fileName, 25);
      case window.innerWidth <= 700:
        return Utils.truncateString(fileName, 35);
      default:
        return fileName;
    }
  };

  const renderFileName = (data) => {
    if (data.uploadProgress === 100 && data?.originalFileName !== "") {
      return (
        <div className="font-MontserratRegular text-ft14-24.5 text-grey-C7CED6">
          {truncateFileName(data.originalFileName)}
        </div>
      );
    }
    return null;
  };

  const renderProgressBar = (data) => {
    if (0 < data?.uploadProgress && data?.uploadProgress < 100) {
      return (
        <label className="progress-bar">
          <progress value={data.uploadProgress || 0} max="100" />
        </label>
      );
    }
    return null;
  };

  const renderProgressText = (data) => {
    if (data.uploadProgress !== 100 && data.uploadProgress !== 0) {
      return (
        <div className="font-PoppinsRegular text-ft12-21 text-blue-2949e5">
          {data.uploadProgress + "%"}
        </div>
      );
    }
    return null;
  };

  const renderDiscardIcon = (data, index) => {
    if (data.uploadProgress !== 0 && data?.originalFileName) {
      return (
        <img
          onClick={() => updateConsent("", data, index, true)}
          className="cursor-pointer"
          src="/images/discard-icon.svg"
          alt="discard"
        />
      );
    }
    return null;
  };

  const handleUpdateConsentWithSubConsents = async (index) => {
    const updatedConsentList = [...consentList];
    const updatedConsent = updatedConsentList[index];
    if (updatedConsent.subConsentDetails.length > 0) {
      const response = await updateConsent("", updatedConsent, index, false);
      if (response?.data?.responseCode === 200) {
        Utils.successToastMessage(
          successMessageConstant.SUNCONSENTS_SAVED_SUCCESSFULLY
        );
      }
    }
  };

  const handleToggleSwitch = async (index) => {
    const updatedToggles = [...toggleSwitches];
    updatedToggles[index] = !updatedToggles[index];
    setToggleSwitches(updatedToggles);
    const updatedConsent = consentList[index];
    await updateConsent(
      "",
      { ...updatedConsent, isRequired: updatedToggles[index], isActive: true },
      index,
      false
    );
  };

  const initialVisibility = Array(10).fill(false);
  const [enableSaveSubconsentBtn, setEnableSaveSubconsentBtn] =
    useState(initialVisibility);

  const handleSaveButtonVisibility = (isChanged, index) => {
    setEnableSaveSubconsentBtn((prevVisibility) => {
      const newVisibility = [...prevVisibility];
      newVisibility[index] = isChanged;
      return newVisibility;
    });
  };

  return (
    <div className="mt-61px w-full max-w-242">
      <div className="border-grey-D9D9D9 font-MontserratSemiBold text-ft19-23 text-black-000000 md:text-ft22-26.82-0.4">
        Select the consent you want to apply
      </div>
      <div className="mt-6.5 flex flex-col gap-2">
        {consentList?.map((data, index) => {
          // Check if all subConsents have unique names
          const areSubConsentNamesUnique =
            new Set(
              data?.subConsentDetails?.map((subConsent) => subConsent.name)
            ).size === data?.subConsentDetails?.length;
          const subConsentNames = data?.subConsentDetails?.filter(
            (subConsent) => subConsent.name
          );
          if (data?.name === consentTypes.ALL_CONSENTS) return;
          return (
            <div
              key={data?._id}
              className="rounded border border-grey-D9D9D9 pb-15px pl-15px pr-2.5 pt-11px md:pl-27px md:pr-13px"
            >
              <div className="flex items-start justify-between ">
                <div className="flex items-start">
                  <label className="checkbox-container mt-13px">
                    <input
                      name="consentCheckBox"
                      id={`consentCheckBox-${index}`}
                      type="checkbox"
                      checked={data.isActive}
                      onChange={(event) =>
                        onClickCheckBoxHandler(event, data, index)
                      }
                    />
                    <span className="checkmark"></span>
                  </label>
                  <div className="ml-9">
                    <p className="font-MontserratMedium text-ft15-26.25 text-black-20">
                      {data?.name}
                    </p>
                    {data?.isRequired || data.isAccepted || data.isActive ? (
                      <div className="flex items-center gap-2">
                        {renderFileName(data)}
                        {renderProgressBar(data)}
                        {renderProgressText(data)}
                        {renderDiscardIcon(data, index)}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="flex">
                  {data?.name !== consentTypes.EMAIL_MARKETING_CONSENT && (
                    <span
                      className={`mr-12 h-fit w-40 cursor-pointer font-MontserratMedium text-ft15-26.25  ${
                        (!areSubConsentNamesUnique ||
                          !enableSaveSubconsentBtn[index]) &&
                        isSubConsentVisible[index]
                          ? "pointer-events-none text-grey-C7C7C7"
                          : "text-purple-6C36B0"
                      }`}
                      onClick={() =>
                        isSubConsentVisible[index]
                          ? handleUpdateConsentWithSubConsents(index)
                          : toggleSubConsentVisibility(index)
                      }
                    >
                      {isSubConsentVisible[index]
                        ? "Save Sub-Consent"
                        : subConsentNames[0]?.name
                          ? "Edit Sub-Consent"
                          : "Create Sub-Consent"}
                    </span>
                  )}
                  <div className="flex-col">
                    <div className="mb-21px flex w-34 justify-between gap-15px">
                      <span className="font-MontserratMedium text-ft15-26.25 text-black-20">
                        Required
                      </span>

                      <ToggleSwitch
                        toggleSwitch={toggleSwitches[index]}
                        handleSwitch={() => handleToggleSwitch(index)}
                      />
                    </div>
                    {data.isAccepted ||
                      (data.isActive && (
                        <>
                          <input
                            type="file"
                            id={`file-input-${index}`}
                            className="hidden"
                            onChange={(event) =>
                              uploadHandler(event, data, index)
                            }
                          />
                          <label htmlFor={`file-input-${index}`}>
                            <div
                              disabled={
                                data.uploadProgress >= 1 &&
                                data.uploadProgress <= 99
                              }
                              className={`${
                                data.uploadProgress >= 1 &&
                                data.uploadProgress <= 99
                                  ? "opacity-75"
                                  : "opacity-100"
                              } grid h-25px w-25 cursor-pointer place-items-center rounded bg-purple-6C36B0 font-PoppinsRegular  text-ft12-21 text-white md:h-11 md:w-34 md:font-PoppinsMedium md:text-ft15-26.25`}
                            >
                              Upload policy
                            </div>
                          </label>
                        </>
                      ))}
                  </div>
                </div>
              </div>
              {isSubConsentVisible[index] && (
                <SubConsent
                  subConsentDetails={data.subConsentDetails}
                  setSubConsentDetails={(newSubConsents) => {
                    const updatedList = [...consentList];
                    updatedList[index].subConsentDetails = newSubConsents;
                    setConsentList(updatedList);
                  }}
                  toggleVisibility={() => toggleSubConsentVisibility(index)}
                  onChange={(isChanged) =>
                    handleSaveButtonVisibility(isChanged, index)
                  }
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

ConsentComponent.propTypes = {
  consentListData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  getConsentList: PropTypes.func.isRequired
};

export default ConsentComponent;
